import { IAppContext } from 'App/AppContext';
import { getLocalStorageData, LocalStorageKeys, setLocalStorageData } from 'utils/localstorage';
import { NavGroupCaptionId, NavItemId } from './navModel';

//
// Which entries and which groups of the menu are closed. One module-level set, shared by every menu
// instance on the page: the compact rail and the flyout are mounted at the same time and must agree.
// useNavModel reads it through useSyncExternalStore.
//
// The set can be more open than storage: a group that holds the page the user is on is opened for
// the visit, without a write, so a reload brings back what the user chose.
//

/** A group in the store. Its own namespace, so a caption id may also be an item id. */
export type NavGroupStoreId = `group:${NavGroupCaptionId}`;

export const navGroupStoreId = (id: NavGroupCaptionId): NavGroupStoreId => `group:${id}` as const;

/**
 * Where an entry that expands in place, or a group, remembers whether it is open, as a LocalStorageKeys
 * value. Empty: the layout has no nested entries and its one group has no caption, so it never closes. An entry or a
 * group without a key still opens and closes, and starts open after a reload.
 */
const expandStorageKeys: Partial<Record<NavItemId | NavGroupStoreId, LocalStorageKeys>> = {};

// The expand flags are a preference of the person, the same in every organization, so they are
// written without the tenant suffix.
const readExpandFlag = (appContext: IAppContext, key: LocalStorageKeys): boolean =>
  getLocalStorageData(appContext, key) !== 'false';

const writeExpandFlag = (appContext: IAppContext, key: LocalStorageKeys, expanded: boolean): void => {
  setLocalStorageData(appContext, key, String(expanded), false);
};

let collapsedIds: ReadonlySet<string> = new Set<string>();
let isLoaded = false;
const listeners = new Set<() => void>();
// The last group opened because the user is on one of its pages. Keeps openGroupForVisit from
// opening the same group twice: the flyout and the mobile panel mount a second menu on every open,
// and without this a group the user had just closed would come back.
let visitGroupId: string | undefined = undefined;

/** Reads the stored flags once. Later calls do nothing. */
export const ensureExpandStateLoaded = (appContext: IAppContext): void => {
  if (isLoaded) {
    return;
  }

  isLoaded = true;
  const collapsed = new Set<string>();
  (Object.entries(expandStorageKeys) as [NavItemId | NavGroupStoreId, LocalStorageKeys][]).forEach(([id, key]) => {
    if (!readExpandFlag(appContext, key)) {
      collapsed.add(id);
    }
  });
  collapsedIds = collapsed;
};

export const subscribeExpandState = (listener: () => void): (() => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

/** The closed entries. The same set instance until something changes, as useSyncExternalStore requires. */
export const getCollapsedIds = (): ReadonlySet<string> => collapsedIds;

/** Opens a closed entry or closes an open one, and keeps that where the entry has a key for it. */
export const toggleExpanded = (appContext: IAppContext, id: string): void => {
  const next = new Set(collapsedIds);
  const nowExpanded = next.has(id);
  if (nowExpanded) {
    next.delete(id);
  } else {
    next.add(id);
  }

  collapsedIds = next;
  const key = expandStorageKeys[id as NavItemId | NavGroupStoreId];
  if (key) {
    writeExpandFlag(appContext, key, nowExpanded);
  }

  listeners.forEach((listener) => listener());
};

/**
 * Opens the group that holds the page the user has just opened, for as long as they stay around it.
 * Nothing is written, so a reload brings back the closed group. Moving between pages of the same
 * group does not open it again, which is what lets the user close it while working in it.
 */
export const openGroupForVisit = (id: string | undefined): void => {
  if (id === visitGroupId) {
    return;
  }

  visitGroupId = id;
  if (id === undefined || !collapsedIds.has(id)) {
    return;
  }

  const next = new Set(collapsedIds);
  next.delete(id);
  collapsedIds = next;

  listeners.forEach((listener) => listener());
};
